// @file init and helper functions for command bar

import { addCommandAICallbacks } from '@@/bits/command_bar/callbacks'
import { captureException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { safeLocalStorage } from '@@/bits/safe_storage'
import type { User } from '@@/types'

// defines valid user properties for CommandBar
enum CommandBarUserPropertyType {
  HasCreatedSlideshowBefore = 'has_created_slideshow_before',
}
/**
 * most nudge event logic is defined in useCommandBarEvents.ts, in response to certain conditions being met
 * however certain nudges are triggered directly in component scripts. For example, padletMiniNudge is triggered
 * directly in SurfaceComposerModalEditor.vue in the handlePastedLink function.
 */
type CommandBarEvent =
  | 'surfaceTableFormatNudge'
  | 'surfaceSlideshowNudge'
  | 'padletMiniNudge'
  | 'wallImportCompletedFeedbackNudge'

/**
 * Dynamic type that generates all possible storage keys for CommandBar events.
 * Based on the template: `commandbar_${eventName}_triggered`
 */
type CommandBarStorageKey = `commandbar_${CommandBarEvent}_triggered`

const ENABLED_PRODUCTS: Array<'nudges' | 'spotlight' | 'checklists' | 'help_hub'> = ['nudges']
const APP = 'mozart'
const MS_PER_DAY = 1000 * 60 * 60 * 24

/**
 * Memoizes the dynamic import + `commandbar.init()` so it only ever runs once.
 *
 * `initCommandBar` is triggered from several independent, overlapping entry points
 * (e.g. `current_user.ts` boots twice, plus the surface store and the dashboard).
 * The `window.CommandBar == null` check alone is not race-safe: `import('commandbar')`
 * is async, so concurrent callers can all pass the check before the first import
 * resolves and sets `window.CommandBar`, each then calling `commandbar.init()`. The
 * SDK warns "CommandBar init was called more than once" for every redundant call.
 * Caching the in-flight promise here guarantees a single initialization.
 */
let commandBarInitPromise: Promise<void> | undefined

/**
 * Initializes the CommandBar if it's enabled for the app.
 * If CommandBar is not already loaded, imports and initializes it with the org ID.
 * Also sets up CommandBar AI callbacks.
 * @see https://www.command.ai/docs/sdk/lifecycle/init/#simplest-initialization
 *
 * @returns Promise<void> Resolves when CommandBar is initialized
 */
async function initCommandBar(): Promise<void> {
  if (!isAppUsing('commandBar')) return

  const COMMAND_BAR_ORG_ID = '88b7c3d1'
  if (COMMAND_BAR_ORG_ID == null) {
    captureException(new Error('CommandBar orgId not found on window object.'))
    return
  }
  if (window.CommandBar == null) {
    commandBarInitPromise ??= (async (): Promise<void> => {
      try {
        const commandbar = await import('commandbar')
        commandbar.init(COMMAND_BAR_ORG_ID)
      } catch (error) {
        // Clear the cache so a later call can retry a transient import failure.
        commandBarInitPromise = undefined
        throw error
      }
    })()
    await commandBarInitPromise
  }

  addCommandAICallbacks()
}

async function bootCommandBarWithUser(currentUser: Partial<User>): Promise<void> {
  if (!isAppUsing('commandBar')) return

  // We do not initialize the command bar if the user is not registered or if the user id is not present
  if (currentUser == null || currentUser.id == null || currentUser.registered_at == null) return

  await initCommandBar()

  let environment = process.env.NODE_ENV ?? 'development'

  if (window.location.hostname.includes('.padlet.io')) {
    environment = 'staging'
  }

  try {
    const userProperties = {
      id: currentUser.id.toString(),
      name: currentUser.name ?? '',
      email: currentUser.email ?? '',
      tenantId: currentUser.tenant_id?.toString() ?? '',
      role: currentUser.role ?? '',
      tenantType: currentUser.tenant_type ?? '',
      paying: currentUser.paying ?? false,
      registeredAt: currentUser.registered_at?.toLocaleString() ?? '',
      environment,
      daysSinceRegistration: -1,
      app: APP,
      lang: currentUser.lang ?? '',
    }
    if (currentUser.registered_at != null) {
      const daysSinceRegistration = Math.floor(
        (Date.now() - new Date(currentUser.registered_at).getTime()) / MS_PER_DAY,
      )
      userProperties.daysSinceRegistration = daysSinceRegistration
    }

    const userIdentifier = `${APP}_${environment}_${currentUser.id}`
    void window.CommandBar.boot(userIdentifier, userProperties, {
      products: ENABLED_PRODUCTS,
    })
  } catch (error) {
    captureException(error)
  }
}

/**
 * Sets a user property in CommandBar.
 * User properties can be used for filtering users within audiences to be targeted with nudges.
 * @see https://www.command.ai/docs/platform/data-types/user-properties/
 *
 * @param userProperty - The CommandBar user property to set
 * @param value - The value to set for the user property
 * @returns void
 */
function setUserProperty(userProperty: CommandBarUserPropertyType, value: any): void {
  if (!isAppUsing('commandBar') || window.CommandBar == null) return

  const userProperties = {
    [userProperty]: value,
  }

  void window.CommandBar.setUserProperties(userProperties)
}

/**
 * Tracks a CommandBar event with optional properties.
 * Events can be used as triggers for nudges
 * @see https://www.command.ai/docs/sdk/events/trackevent/
 *
 * @param event - The CommandBar event to track
 * @param properties - Optional key-value pairs of additional properties to track with the event
 * @returns void
 */
function trackEvent(event: CommandBarEvent, properties: Record<string, string> = {}): void {
  if (!isAppUsing('commandBar') || window.CommandBar == null) return
  window.CommandBar.trackEvent(event, properties)
}

/**
 * Resets a CommandBar nudge so the next trigger shows the first step again.
 *
 * @param nudgeId - Numeric ID of the nudge defined in CommandBar.
 * @param step - Optional step index to reset; defaults to the entire nudge.
 */
function resetNudge(nudgeId: number, step?: number): void {
  if (!isAppUsing('commandBar') || window.CommandBar == null) return
  window.CommandBar.resetNudge?.(nudgeId, step)
}

/**
 * Tracks a CommandBar event only once per user by storing a flag in localStorage.
 * Subsequent calls with the same eventName will not track the event again.
 *
 * @param eventName - The CommandBar event name to track
 * @param setLocalStorage - Whether to set a flag in localStorage to prevent tracking the event again.
 * This is set to false for nudge events dependent on callbacks
 * To ensure that callbacks set this local storage directly.
 * If you'd like to set the flag manually, you can do so after the action
 * for example, see: convertWallToTableFormat() in tours/table_format_release/actions.ts
 * @param localStorageKeySuffix - Optional suffix to append to the localStorage key.
 */

const trackEventOnlyOnce = (
  eventName: CommandBarEvent,
  {
    setLocalStorage = true,
    localStorageKeySuffix,
  }: {
    setLocalStorage?: boolean
    localStorageKeySuffix?: string | number
  } = {},
): void => {
  const key: `${CommandBarStorageKey}:${string}` | CommandBarStorageKey = localStorageKeySuffix
    ? `commandbar_${eventName}_triggered:${localStorageKeySuffix}`
    : `commandbar_${eventName}_triggered`
  if (safeLocalStorage.getItem(key)) {
    return // Already tracked
  }

  trackEvent(eventName)
  if (setLocalStorage) {
    safeLocalStorage.setItem(key, 'true')
  }
}

export {
  bootCommandBarWithUser,
  CommandBarUserPropertyType,
  initCommandBar,
  resetNudge,
  setUserProperty,
  trackEvent,
  trackEventOnlyOnce,
}
export type { CommandBarEvent, CommandBarStorageKey }
